import Cookies from 'js-cookie';
import { useEffect } from 'react';
import logConsentDecisionMade from '@alltrails/amplitude/events/logConsentDecisionMade';
import setUserProperties from '@alltrails/amplitude/utils/setUserProperties';
import { useExperiments } from '@alltrails/experiments';
import type OsanoConsentObject from '@alltrails/types/OsanoConsentObject';

const setPixelConsent = (consentObject: OsanoConsentObject) => {
  if (typeof fbq !== 'undefined') {
    fbq('consent', consentObject.MARKETING === 'ACCEPT' ? 'grant' : 'revoke');
  }
};

const setAppsflyerMeasurement = (consentObject: OsanoConsentObject) => {
  if (consentObject.ANALYTICS === 'ACCEPT') {
    window?.AF_SDK?.PLUGINS?.PBA?.enableMeasurement();
  } else {
    window?.AF_SDK?.PLUGINS?.PBA?.disableMeasurement();
  }
};

// Track Osano consent decisions
const useOsano = () => {
  const experiments = useExperiments();
  const isDebugLoggingEnabled = experiments['web-osano-block-logs']?.value === 'on';

  useEffect(() => {
    const setConsentUserProperty = (consentObject?: OsanoConsentObject) => {
      setUserProperties({ ...consentObject });
    };

    const handleOsanoInit = (consentObject?: OsanoConsentObject) => {
      setConsentUserProperty(consentObject);

      if (!consentObject) return;

      setPixelConsent(consentObject);
    };

    const handleOsanoConsentSaved = (consentObject: OsanoConsentObject) => {
      const consent = Cookies.get('at_osano');
      const stringConsent = JSON.stringify({ consent: consentObject });

      setPixelConsent(consentObject);

      if (consent === stringConsent) return;
      setConsentUserProperty(consentObject);

      logConsentDecisionMade({
        analytics: consentObject.ANALYTICS,
        essential: consentObject.ESSENTIAL,
        marketing: consentObject.MARKETING,
        optout: consentObject.OPT_OUT,
        personalization: consentObject.PERSONALIZATION,
        storage: consentObject.STORAGE
      });

      window.DD_RUM?.setTrackingConsent?.(consentObject.ANALYTICS === 'ACCEPT' ? 'granted' : 'not-granted');

      setAppsflyerMeasurement(consentObject);

      Cookies.set('at_osano', stringConsent);
    };

    window.Osano?.cm?.addEventListener('osano-cm-consent-saved', handleOsanoConsentSaved);
    window.Osano?.cm?.addEventListener('osano-cm-initialized', handleOsanoInit);

    let removeDebugBlockListeners: (() => void) | undefined;
    if (isDebugLoggingEnabled) {
      const handleScriptBlocked = (item: string) => {
        console.log(`Script blocked: ${item}`);
      };
      const handleCookieBlocked = (item: string) => {
        console.log(`Cookie blocked: ${item}`);
      };
      const handleIframeBlocked = (item: string) => {
        console.log(`Iframe blocked: ${item}`);
      };
      window.Osano?.cm?.addEventListener('osano-cm-script-blocked', handleScriptBlocked);
      window.Osano?.cm?.addEventListener('osano-cm-cookie-blocked', handleCookieBlocked);
      window.Osano?.cm?.addEventListener('osano-cm-iframe-blocked', handleIframeBlocked);
      removeDebugBlockListeners = () => {
        window.Osano?.cm?.removeEventListener('osano-cm-script-blocked', handleScriptBlocked);
        window.Osano?.cm?.removeEventListener('osano-cm-cookie-blocked', handleCookieBlocked);
        window.Osano?.cm?.removeEventListener('osano-cm-iframe-blocked', handleIframeBlocked);
      };
    }

    return () => {
      window.Osano?.cm?.removeEventListener('osano-cm-consent-saved', handleOsanoConsentSaved);
      window.Osano?.cm?.removeEventListener('osano-cm-initialized', handleOsanoInit);
      removeDebugBlockListeners?.();
    };
  }, [isDebugLoggingEnabled]);
};

export default useOsano;
